/**
 * Unsubscribe.js - Newsletter Unsubscribe Page
 * Author: Markus.van.Kempen@gmail.com
 * Date: 4th Dec 2025
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from './LoadingSpinner';
import './Unsubscribe.css';

/** Deep-link to Personal Info tab and scroll to the newsletter checkbox */
const PROFILE_NEWSLETTER_PATH = '/profile?tab=personal#newsletter-settings';

const Unsubscribe = () => {
    const [searchParams] = useSearchParams();
    const { currentUser } = useAuth();
    const [status, setStatus] = useState('loading'); // loading, success, error, already
    const [message, setMessage] = useState('');
    const [email, setEmail] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const unsubscribeUser = async () => {
            const token = searchParams.get('token');
            const emailParam = searchParams.get('email');

            if (!token && !emailParam) {
                setStatus('error');
                setMessage('Invalid unsubscribe link. Please contact support.');
                return;
            }

            try {
                const response = await fetch('/api/newsletter/unsubscribe', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ token, email: emailParam })
                });

                const data = await response.json();

                if (response.ok) {
                    if (data.alreadyUnsubscribed) {
                        setStatus('already');
                        setMessage('You were already unsubscribed from our newsletter.');
                    } else {
                        setStatus('success');
                        setMessage('You have been successfully unsubscribed from our newsletter.');
                    }
                    setEmail(data.email || emailParam);
                } else {
                    setStatus('error');
                    setMessage(data.error || 'Failed to unsubscribe. Please try again or contact support.');
                }
            } catch (error) {
                console.error('Unsubscribe error:', error);
                setStatus('error');
                setMessage('An error occurred. Please try again later.');
            }
        };

        unsubscribeUser();
    }, [searchParams]);

    return (
        <div className="unsubscribe-container">
            <div className="unsubscribe-card">
                <div className="unsubscribe-header">
                    <h1>Newsletter Unsubscribe</h1>
                </div>

                <div className="unsubscribe-content">
                    {status === 'loading' && (
                        <div className="unsubscribe-loading">
                            <LoadingSpinner size="medium" message="Processing your request..." />
                        </div>
                    )}

                    {status === 'success' && (
                        <div className="unsubscribe-success">
                            <div className="success-icon">✓</div>
                            <h2>Unsubscribed Successfully</h2>
                            <p>{message}</p>
                            {email && <p className="email-info">Email: <strong>{email}</strong></p>}
                            <p className="info-text">
                                We're sorry to see you go! You will no longer receive newsletters from Cranford Publications.
                            </p>
                            <p className="info-text">
                                If you change your mind, you can always resubscribe from our website.
                            </p>
                        </div>
                    )}

                    {status === 'already' && (
                        <div className="unsubscribe-already">
                            <div className="info-icon">ℹ</div>
                            <h2>Already Unsubscribed</h2>
                            <p>{message}</p>
                            {email && <p className="email-info">Email: <strong>{email}</strong></p>}
                        </div>
                    )}

                    {status === 'error' && (
                        <div className="unsubscribe-error">
                            <div className="error-icon">✗</div>
                            <h2>Unsubscribe Failed</h2>
                            <p>{message}</p>
                            <div className="unsubscribe-error-fallback">
                                <p className="info-text unsubscribe-fallback-heading">
                                    If you have an account with us, you can turn off newsletter emails from your profile:
                                    open <strong>Personal Info</strong>, uncheck <strong>Subscribe to our newsletter</strong>,
                                    then click <strong>Save Changes</strong>. (If that box is greyed out, confirm your email address first.)
                                </p>
                                <div className="unsubscribe-error-actions">
                                    {currentUser ? (
                                        <button
                                            type="button"
                                            className="btn-secondary"
                                            onClick={() => navigate(PROFILE_NEWSLETTER_PATH)}
                                        >
                                            Open profile — newsletter setting
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            className="btn-secondary"
                                            onClick={() =>
                                                navigate(
                                                    `/login?returnUrl=${encodeURIComponent(PROFILE_NEWSLETTER_PATH)}`
                                                )
                                            }
                                        >
                                            Sign in to manage newsletter preferences
                                        </button>
                                    )}
                                </div>
                            </div>
                            <p className="info-text">
                                If you continue to experience issues, please contact us at{' '}
                                <a href="mailto:info@cranfordpub.ca">info@cranfordpub.ca</a>
                            </p>
                        </div>
                    )}

                    <div className="unsubscribe-actions">
                        <button
                            className="btn-primary"
                            onClick={() => navigate('/')}
                        >
                            Return to Homepage
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Unsubscribe;
